import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Clipboard, Download, FileSpreadsheet, Plus, Trash2, Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
interface RowData {
  id: string;
  name: string;
  quantity: number;
  // Raw input values
  rawWeight: number;
  rawLength: number;
  rawWidth: number;
  rawHeight: number;
  // Calculated metric values
  weightMT: number;
  lengthM: number;
  widthM: number;
  heightM: number;
  totalArea: number;
  totalWeight: number;
}
interface ColumnMap {
  name: string;
  quantity: string;
  rawWeight: string;
  rawLength: string;
  rawWidth: string;
  rawHeight: string;
}
export function TableInputStep({
  formData,
  setFormData
}) {
  const [rows, setRows] = useState<RowData[]>([getEmptyRow()]);
  const [pasteContent, setPasteContent] = useState('');
  const [showPasteDialog, setShowPasteDialog] = useState(false);
  const [showFileDialog, setShowFileDialog] = useState(false);
  const [excelData, setExcelData] = useState<any[]>([]);
  const [columnMap, setColumnMap] = useState<ColumnMap>({
    name: '',
    quantity: '',
    rawWeight: '',
    rawLength: '',
    rawWidth: '',
    rawHeight: ''
  });
  const [availableColumns, setAvailableColumns] = useState<string[]>([]);
  const [inputMethod, setInputMethod] = useState<'manual' | 'paste' | 'upload'>('manual');
  const weightConversion = formData.weightConversion || 0.000453592; // Default lbs to mT
  const lengthConversion = formData.lengthConversion || 0.3048; // Default ft to m
  function getEmptyRow(): RowData {
    return {
      id: Math.random().toString(36).substr(2, 9),
      name: '',
      quantity: 1,
      rawWeight: 0,
      rawLength: 0,
      rawWidth: 0,
      rawHeight: 0,
      weightMT: 0,
      lengthM: 0,
      widthM: 0,
      heightM: 0,
      totalArea: 0,
      totalWeight: 0
    };
  }
  const calculateDerivedValues = (row: Partial<RowData>): RowData => {
    const weightMT = (row.rawWeight || 0) * weightConversion;
    const lengthM = (row.rawLength || 0) * lengthConversion;
    const widthM = (row.rawWidth || 0) * lengthConversion;
    const heightM = (row.rawHeight || 0) * lengthConversion;
    const quantity = row.quantity || 1;
    return {
      ...row,
      weightMT,
      lengthM,
      widthM,
      heightM,
      totalArea: quantity * lengthM * widthM,
      totalWeight: quantity * weightMT
    } as RowData;
  };
  const handlePasteConfirm = () => {
    if (!pasteContent.trim()) {
      setShowPasteDialog(false);
      return;
    }
    // Process the pasted content
    const lines = pasteContent.trim().split('\n');
    const headers = lines[0].split('\t');
    // If there's only one line, it might be just data without headers
    const hasHeaders = lines.length > 1 && !headers.every(h => !isNaN(Number(h.trim()))) && headers.length > 1;
    const dataStartIndex = hasHeaders ? 1 : 0;
    // Try to auto-detect column types if we have headers
    let detectedMap: ColumnMap = {
      name: '',
      quantity: '',
      rawWeight: '',
      rawLength: '',
      rawWidth: '',
      rawHeight: ''
    };
    if (hasHeaders) {
      headers.forEach((header, index) => {
        const h = header.toLowerCase().trim();
        if (h.includes('name') || h.includes('description') || h.includes('item')) {
          detectedMap.name = index.toString();
        } else if (h.includes('qty') || h.includes('quantity') || h.includes('count')) {
          detectedMap.quantity = index.toString();
        } else if (h.includes('weight') || h.includes('mass')) {
          detectedMap.rawWeight = index.toString();
        } else if (h.includes('length') || h.includes('long')) {
          detectedMap.rawLength = index.toString();
        } else if (h.includes('width') || h.includes('wide')) {
          detectedMap.rawWidth = index.toString();
        } else if (h.includes('height') || h.includes('tall')) {
          detectedMap.rawHeight = index.toString();
        }
      });
    } else if (headers.length >= 5) {
      // If no headers but enough columns, make a guess based on position
      detectedMap = {
        name: '0',
        rawWeight: '1',
        rawLength: '2',
        rawWidth: '3',
        rawHeight: '4',
        quantity: headers.length > 5 ? '5' : ''
      };
    }
    // Process data rows
    const newRows: RowData[] = [];
    for (let i = dataStartIndex; i < lines.length; i++) {
      const cells = lines[i].split('\t');
      if (cells.length < 2) continue; // Skip lines with insufficient data
      const rowData: Partial<RowData> = {
        id: Math.random().toString(36).substr(2, 9)
      };
      // Map columns based on detected or assumed positions
      if (detectedMap.name && cells[parseInt(detectedMap.name)]) {
        rowData.name = cells[parseInt(detectedMap.name)].trim();
      }
      if (detectedMap.quantity && cells[parseInt(detectedMap.quantity)]) {
        const qty = parseFloat(cells[parseInt(detectedMap.quantity)]);
        rowData.quantity = isNaN(qty) ? 1 : qty;
      } else {
        rowData.quantity = 1;
      }
      if (detectedMap.rawWeight && cells[parseInt(detectedMap.rawWeight)]) {
        const weight = parseFloat(cells[parseInt(detectedMap.rawWeight)]);
        rowData.rawWeight = isNaN(weight) ? 0 : weight;
      }
      if (detectedMap.rawLength && cells[parseInt(detectedMap.rawLength)]) {
        const length = parseFloat(cells[parseInt(detectedMap.rawLength)]);
        rowData.rawLength = isNaN(length) ? 0 : length;
      }
      if (detectedMap.rawWidth && cells[parseInt(detectedMap.rawWidth)]) {
        const width = parseFloat(cells[parseInt(detectedMap.rawWidth)]);
        rowData.rawWidth = isNaN(width) ? 0 : width;
      }
      if (detectedMap.rawHeight && cells[parseInt(detectedMap.rawHeight)]) {
        const height = parseFloat(cells[parseInt(detectedMap.rawHeight)]);
        rowData.rawHeight = isNaN(height) ? 0 : height;
      }
      newRows.push(calculateDerivedValues(rowData));
    }
    if (newRows.length > 0) {
      setRows(newRows);
      setInputMethod('paste');
    }
    setShowPasteDialog(false);
    setPasteContent('');
  };
  const handleExcelUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      // In a real implementation, you would use a library like SheetJS/xlsx
      // For this demo, we'll simulate parsing by reading as text
      const text = await file.text();
      const lines = text.split('\n');
      if (lines.length < 2) {
        alert('File appears to be empty or invalid');
        return;
      }
      // Get headers from first line
      const headers = lines[0].split(',').map(h => h.trim());
      setAvailableColumns(headers);
      // Process data rows
      const data = [];
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        const values = lines[i].split(',');
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index]?.trim() || '';
        });
        data.push(row);
      }
      setExcelData(data);
      // Reset column mapping
      setColumnMap({
        name: '',
        quantity: '',
        rawWeight: '',
        rawLength: '',
        rawWidth: '',
        rawHeight: ''
      });
      // Try to auto-detect columns
      headers.forEach(header => {
        const h = header.toLowerCase();
        if (h.includes('name') || h.includes('description') || h.includes('item')) {
          setColumnMap(prev => ({
            ...prev,
            name: header
          }));
        } else if (h.includes('qty') || h.includes('quantity') || h.includes('count')) {
          setColumnMap(prev => ({
            ...prev,
            quantity: header
          }));
        } else if (h.includes('weight') || h.includes('mass')) {
          setColumnMap(prev => ({
            ...prev,
            rawWeight: header
          }));
        } else if (h.includes('length') || h.includes('long')) {
          setColumnMap(prev => ({
            ...prev,
            rawLength: header
          }));
        } else if (h.includes('width') || h.includes('wide')) {
          setColumnMap(prev => ({
            ...prev,
            rawWidth: header
          }));
        } else if (h.includes('height') || h.includes('tall')) {
          setColumnMap(prev => ({
            ...prev,
            rawHeight: header
          }));
        }
      });
      setShowFileDialog(true);
    } catch (error) {
      console.error('Error processing file:', error);
      alert('Error processing file. Please make sure it is a valid CSV or Excel file.');
    }
  };
  const handleImportExcel = () => {
    if (excelData.length === 0) {
      setShowFileDialog(false);
      return;
    }
    const newRows: RowData[] = excelData.map(row => {
      const rowData: Partial<RowData> = {
        id: Math.random().toString(36).substr(2, 9),
        name: columnMap.name ? String(row[columnMap.name] || '') : '',
        quantity: columnMap.quantity ? Number(row[columnMap.quantity]) || 1 : 1,
        rawWeight: columnMap.rawWeight ? Number(row[columnMap.rawWeight]) || 0 : 0,
        rawLength: columnMap.rawLength ? Number(row[columnMap.rawLength]) || 0 : 0,
        rawWidth: columnMap.rawWidth ? Number(row[columnMap.rawWidth]) || 0 : 0,
        rawHeight: columnMap.rawHeight ? Number(row[columnMap.rawHeight]) || 0 : 0
      };
      return calculateDerivedValues(rowData);
    }).filter(row => row.name || row.rawWeight > 0 || row.rawLength > 0);
    if (newRows.length > 0) {
      setRows(newRows);
      setInputMethod('upload');
    }
    setShowFileDialog(false);
  };
  const handleChange = (id: string, field: keyof RowData, value: string | number) => {
    setRows(current => current.map(row => {
      if (row.id !== id) return row;
      const updatedRow = {
        ...row,
        [field]: typeof value === 'string' ? value : Number(value) || 0
      };
      return calculateDerivedValues(updatedRow);
    }));
  };
  const addRow = () => {
    setRows(current => [...current, getEmptyRow()]);
  };
  const removeRow = (id: string) => {
    setRows(current => current.filter(row => row.id !== id));
  };
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      tableData: rows
    }));
  }, [rows]);
  return <div className="space-y-4" data-id="element-269">
      <div className="flex justify-between items-center mb-4" data-id="element-270">
        <Tabs defaultValue="manual" className="w-full" onValueChange={v => setInputMethod(v as any)} data-id="element-271">
          <TabsList className="grid grid-cols-3 w-full max-w-md" data-id="element-272">
            <TabsTrigger value="manual" data-id="element-273">Manual Entry</TabsTrigger>
            <TabsTrigger value="paste" data-id="element-274">Paste from Excel</TabsTrigger>
            <TabsTrigger value="upload" data-id="element-275">Upload Excel</TabsTrigger>
          </TabsList>
          <TabsContent value="manual" className="mt-4" data-id="element-276">
            <p className="text-sm text-gray-600 mb-2" data-id="element-277">
              Add cargo items one by one using the form below.
            </p>
          </TabsContent>
          <TabsContent value="paste" className="mt-4" data-id="element-278">
            <div className="flex items-center gap-4" data-id="element-279">
              <Button onClick={() => setShowPasteDialog(true)} variant="outline" className="flex items-center gap-2" data-id="element-280">
                <Clipboard className="h-4 w-4" data-id="element-281" />
                Paste Data
              </Button>
              <p className="text-sm text-gray-600" data-id="element-282">
                Copy from Excel (Ctrl+C) and paste your data here
              </p>
            </div>
          </TabsContent>
          <TabsContent value="upload" className="mt-4" data-id="element-283">
            <div className="flex items-center gap-4" data-id="element-284">
              <Button variant="outline" className="flex items-center gap-2" onClick={() => document.getElementById('excel-upload')?.click()} data-id="element-285">
                <FileSpreadsheet className="h-4 w-4" data-id="element-286" />
                Select Excel File
              </Button>
              <Input id="excel-upload" type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleExcelUpload} data-id="element-287" />
              <p className="text-sm text-gray-600" data-id="element-288">
                Upload an Excel or CSV file with your cargo data
              </p>
            </div>
          </TabsContent>
        </Tabs>
        <Button onClick={addRow} variant="outline" className="flex items-center gap-2" data-id="element-289">
          <Plus className="h-4 w-4" data-id="element-290" />
          Add Row
        </Button>
      </div>

      {/* Compact Data Table */}
      <div className="border rounded-lg overflow-hidden" data-id="element-291">
        <div className="overflow-x-auto" data-id="element-292">
          <Table data-id="element-293">
            <TableHeader data-id="element-294">
              <TableRow className="bg-gray-50" data-id="element-295">
                <TableHead className="w-[300px]" data-id="element-296">Name</TableHead>
                <TableHead className="w-[80px]" data-id="element-297">Qty</TableHead>
                <TableHead className="w-[100px]" data-id="element-298">Weight</TableHead>
                <TableHead className="w-[100px]" data-id="element-299">Length</TableHead>
                <TableHead className="w-[100px]" data-id="element-300">Width</TableHead>
                <TableHead className="w-[100px]" data-id="element-301">Height</TableHead>
                <TableHead className="w-[100px] bg-blue-50" data-id="element-302">
                  Weight [mT]
                </TableHead>
                <TableHead className="w-[100px] bg-blue-50" data-id="element-303">
                  Area [m²]
                </TableHead>
                <TableHead className="w-[60px]" data-id="element-304"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-id="element-305">
              {rows.map(row => <TableRow key={row.id} data-id="element-306">
                  <TableCell data-id="element-307">
                    <Input value={row.name} onChange={e => handleChange(row.id, 'name', e.target.value)} className="w-full" data-id="element-308" />
                  </TableCell>
                  <TableCell data-id="element-309">
                    <Input type="number" value={row.quantity} onChange={e => handleChange(row.id, 'quantity', e.target.value)} className="w-full" min="1" data-id="element-310" />
                  </TableCell>
                  <TableCell data-id="element-311">
                    <Input type="number" value={row.rawWeight} onChange={e => handleChange(row.id, 'rawWeight', e.target.value)} className="w-full" data-id="element-312" />
                  </TableCell>
                  <TableCell data-id="element-313">
                    <Input type="number" value={row.rawLength} onChange={e => handleChange(row.id, 'rawLength', e.target.value)} className="w-full" data-id="element-314" />
                  </TableCell>
                  <TableCell data-id="element-315">
                    <Input type="number" value={row.rawWidth} onChange={e => handleChange(row.id, 'rawWidth', e.target.value)} className="w-full" data-id="element-316" />
                  </TableCell>
                  <TableCell data-id="element-317">
                    <Input type="number" value={row.rawHeight} onChange={e => handleChange(row.id, 'rawHeight', e.target.value)} className="w-full" data-id="element-318" />
                  </TableCell>
                  <TableCell className="bg-blue-50 text-right font-medium" data-id="element-319">
                    {row.totalWeight.toFixed(2)}
                  </TableCell>
                  <TableCell className="bg-blue-50 text-right font-medium" data-id="element-320">
                    {row.totalArea.toFixed(2)}
                  </TableCell>
                  <TableCell data-id="element-321">
                    <Button variant="ghost" size="icon" onClick={() => removeRow(row.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-id="element-322">
                      <Trash2 className="h-4 w-4" data-id="element-323" />
                    </Button>
                  </TableCell>
                </TableRow>)}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Paste Dialog */}
      <Dialog open={showPasteDialog} onOpenChange={setShowPasteDialog} data-id="element-324">
        <DialogContent className="sm:max-w-[600px]" data-id="element-325">
          <DialogHeader data-id="element-326">
            <DialogTitle data-id="element-327">Paste Data from Excel</DialogTitle>
            <DialogDescription data-id="element-328">
              Copy your data from Excel (Ctrl+C) and paste it below (Ctrl+V).
              The first row can optionally contain headers.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4" data-id="element-329">
            <textarea className="min-h-[200px] p-3 border border-blue-200 rounded-md font-mono text-sm" placeholder="Paste your Excel data here..." value={pasteContent} onChange={e => setPasteContent(e.target.value)} onPaste={e => {
            // This allows the default paste behavior to work
            setTimeout(() => {
              setPasteContent(e.currentTarget.value);
            }, 0);
          }} data-id="element-330" />
            <div className="bg-amber-50 p-3 rounded-md text-sm" data-id="element-331">
              <p className="font-medium text-amber-800" data-id="element-332">Format Tips:</p>
              <p className="text-amber-700" data-id="element-333">
                • Copy directly from Excel (including headers if available)
                <br data-id="element-334" />
                • Data should include: name, weight, dimensions, and quantity
                <br data-id="element-335" />• The system will try to auto-detect your columns
              </p>
            </div>
          </div>
          <DialogFooter data-id="element-336">
            <Button variant="outline" onClick={() => setShowPasteDialog(false)} data-id="element-337">
              Cancel
            </Button>
            <Button onClick={handlePasteConfirm} className="bg-[#1a365d]" data-id="element-338">
              Import Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Excel Column Mapping Dialog */}
      <Dialog open={showFileDialog} onOpenChange={setShowFileDialog} data-id="element-339">
        <DialogContent className="sm:max-w-[600px]" data-id="element-340">
          <DialogHeader data-id="element-341">
            <DialogTitle data-id="element-342">Map Excel Columns</DialogTitle>
            <DialogDescription data-id="element-343">
              Please verify the column mapping below. Select which columns from
              your Excel file correspond to each data field.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4" data-id="element-344">
            <div className="grid grid-cols-2 gap-4" data-id="element-345">
              <div className="space-y-2" data-id="element-346">
                <Label data-id="element-347">Name/Description</Label>
                <Select value={columnMap.name} onValueChange={v => setColumnMap(prev => ({
                ...prev,
                name: v
              }))} data-id="element-348">
                  <SelectTrigger className="border-blue-200" data-id="element-349">
                    <SelectValue placeholder="Select column" data-id="element-350" />
                  </SelectTrigger>
                  <SelectContent data-id="element-351">
                    <SelectItem value="" data-id="element-352">Not mapped</SelectItem>
                    {availableColumns.map(col => <SelectItem key={col} value={col} data-id="element-353">
                        {col}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2" data-id="element-354">
                <Label data-id="element-355">Quantity</Label>
                <Select value={columnMap.quantity} onValueChange={v => setColumnMap(prev => ({
                ...prev,
                quantity: v
              }))} data-id="element-356">
                  <SelectTrigger className="border-blue-200" data-id="element-357">
                    <SelectValue placeholder="Select column" data-id="element-358" />
                  </SelectTrigger>
                  <SelectContent data-id="element-359">
                    <SelectItem value="" data-id="element-360">Not mapped (default: 1)</SelectItem>
                    {availableColumns.map(col => <SelectItem key={col} value={col} data-id="element-361">
                        {col}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2" data-id="element-362">
                <Label data-id="element-363">Weight</Label>
                <Select value={columnMap.rawWeight} onValueChange={v => setColumnMap(prev => ({
                ...prev,
                rawWeight: v
              }))} data-id="element-364">
                  <SelectTrigger className="border-blue-200" data-id="element-365">
                    <SelectValue placeholder="Select column" data-id="element-366" />
                  </SelectTrigger>
                  <SelectContent data-id="element-367">
                    <SelectItem value="" data-id="element-368">Not mapped</SelectItem>
                    {availableColumns.map(col => <SelectItem key={col} value={col} data-id="element-369">
                        {col}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2" data-id="element-370">
                <Label data-id="element-371">Length</Label>
                <Select value={columnMap.rawLength} onValueChange={v => setColumnMap(prev => ({
                ...prev,
                rawLength: v
              }))} data-id="element-372">
                  <SelectTrigger className="border-blue-200" data-id="element-373">
                    <SelectValue placeholder="Select column" data-id="element-374" />
                  </SelectTrigger>
                  <SelectContent data-id="element-375">
                    <SelectItem value="" data-id="element-376">Not mapped</SelectItem>
                    {availableColumns.map(col => <SelectItem key={col} value={col} data-id="element-377">
                        {col}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2" data-id="element-378">
                <Label data-id="element-379">Width</Label>
                <Select value={columnMap.rawWidth} onValueChange={v => setColumnMap(prev => ({
                ...prev,
                rawWidth: v
              }))} data-id="element-380">
                  <SelectTrigger className="border-blue-200" data-id="element-381">
                    <SelectValue placeholder="Select column" data-id="element-382" />
                  </SelectTrigger>
                  <SelectContent data-id="element-383">
                    <SelectItem value="" data-id="element-384">Not mapped</SelectItem>
                    {availableColumns.map(col => <SelectItem key={col} value={col} data-id="element-385">
                        {col}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2" data-id="element-386">
                <Label data-id="element-387">Height</Label>
                <Select value={columnMap.rawHeight} onValueChange={v => setColumnMap(prev => ({
                ...prev,
                rawHeight: v
              }))} data-id="element-388">
                  <SelectTrigger className="border-blue-200" data-id="element-389">
                    <SelectValue placeholder="Select column" data-id="element-390" />
                  </SelectTrigger>
                  <SelectContent data-id="element-391">
                    <SelectItem value="" data-id="element-392">Not mapped</SelectItem>
                    {availableColumns.map(col => <SelectItem key={col} value={col} data-id="element-393">
                        {col}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {excelData.length > 0 && <div className="mt-4" data-id="element-394">
                <h4 className="font-medium mb-2" data-id="element-395">Preview (First 3 rows):</h4>
                <div className="border border-blue-100 rounded overflow-x-auto max-h-[200px]" data-id="element-396">
                  <table className="min-w-full text-sm" data-id="element-397">
                    <thead data-id="element-398">
                      <tr className="bg-blue-50" data-id="element-399">
                        {availableColumns.map(col => <th key={col} className="p-2 text-left border-b border-blue-100 text-blue-700" data-id="element-400">
                            {col}
                          </th>)}
                      </tr>
                    </thead>
                    <tbody data-id="element-401">
                      {excelData.slice(0, 3).map((row, idx) => <tr key={idx} className="border-b border-blue-50" data-id="element-402">
                          {availableColumns.map(col => <td key={col} className="p-2" data-id="element-403">
                              {row[col]}
                            </td>)}
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>}
          </div>
          <DialogFooter data-id="element-404">
            <Button variant="outline" onClick={() => setShowFileDialog(false)} data-id="element-405">
              Cancel
            </Button>
            <Button onClick={handleImportExcel} className="bg-[#1a365d]" data-id="element-406">
              Import Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>;
}